import type {
    Assignee,
    AssigneeRole,
    ControlDraft,
    Participant,
} from "../../../api/documentWorkflowTypes";
import type { ExpediteSwitch } from "../../../api/workflowTemplates";

/**
 * The RFI composer's fields that have no column of their own live in
 * `draft.custom_fields` under these keys, so the record, its properties
 * and the composer all read the same names.
 */
export const RFI_FIELD = {
    references: "References",
    manager: "RFI manager",
    costImpact: "Cost impact",
    scheduleImpact: "Schedule impact",
    discipline: "Discipline",
    priority: "Priority",
    location: "Location",
} as const;

export type RfiFieldKey = (typeof RFI_FIELD)[keyof typeof RFI_FIELD];

/** Keys the record renders on its own (chips, the manager row), not as generic custom fields. */
export const RFI_OWN_FIELDS: ReadonlySet<string> = new Set([
    RFI_FIELD.references,
    RFI_FIELD.manager,
]);

/**
 * The fields of a workflow's Expedite review (D12), as the server names them (`ExpediteSwitch`):
 * the switch and its two companions, which the composer draws together as one block. Empty for a
 * workflow that names no switch; no field is known here by its key.
 */
export const expediteKeys = (expedite: ExpediteSwitch | null): ReadonlySet<string> =>
    new Set(
        expedite
            ? [expedite.field, expedite.holds_up, expedite.reason].filter(
                  (key): key is string => !!key,
              )
            : [],
    );

/** Whether a due date asks for an answer sooner than the default the contract gives (ISO days). */
export const asksSooner = (due: string | null, contractDate: string | null | undefined): boolean =>
    !!due && !!contractDate && due < contractDate;

export const IMPACT_OPTIONS = ["TBD", "Yes", "No"] as const;
export const PRIORITY_OPTIONS = ["Low", "Normal", "High"] as const;

const REFERENCE_SEPARATOR = ", ";

/** A custom field as text, or empty when unset. */
export const rfiField = (draft: Pick<ControlDraft, "custom_fields">, key: RfiFieldKey): string => {
    const value = draft.custom_fields[key];
    return value == null ? "" : String(value);
};

/** The drawing and spec references on a draft (`A5.01`, `07 92 00 §2.02`). */
export const draftReferences = (draft: Pick<ControlDraft, "custom_fields">): string[] =>
    rfiField(draft, RFI_FIELD.references)
        .split(REFERENCE_SEPARATOR)
        .map((code) => code.trim())
        .filter(Boolean);

/** Writes the references back; an empty list removes the field. */
export function withRfiField(
    fields: ControlDraft["custom_fields"],
    key: RfiFieldKey,
    value: string,
): ControlDraft["custom_fields"] {
    const next = { ...fields };
    if (value.trim()) next[key] = value.trim();
    else delete next[key];
    return next;
}

export const withDraftReferences = (
    fields: ControlDraft["custom_fields"],
    codes: string[],
): ControlDraft["custom_fields"] =>
    withRfiField(
        fields,
        RFI_FIELD.references,
        Array.from(new Set(codes.map((c) => c.trim()).filter(Boolean))).join(REFERENCE_SEPARATOR),
    );

export const isEmail = (text: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text.trim());

export const memberAssignee = (member: Participant, role: AssigneeRole): Assignee => ({
    id: member.id,
    name: member.name,
    email: member.email ?? null,
    company: member.company ?? null,
    external: false,
    role,
});

/** Someone outside the project, reached by email; they show as "outside". */
export const outsideAssignee = (email: string, role: AssigneeRole): Assignee => ({
    id: null,
    name: email.trim(),
    email: email.trim(),
    company: null,
    external: true,
    role,
});

export const assigneeLabel = (assignee: Assignee): string =>
    assignee.external ? `${assignee.email || assignee.name} · outside` : assignee.name;

/**
 * A whole company as a watcher (CONTRACT_V31 §6.3): no member id or email; the server reaches its
 * members at each notice.
 */
export const companyAssignee = (
    company: { id: string; name: string },
    role: AssigneeRole = "watcher",
): Assignee => ({ id: null, company_id: company.id, name: company.name, external: false, role });

/**
 * One identity per person across roles: the member id, the company for a whole company, or the
 * email for outside people.
 */
export const assigneeKey = (
    assignee: Pick<Assignee, "id" | "email" | "name"> & Pick<Partial<Assignee>, "company_id">,
): string =>
    assignee.id ||
    (assignee.company_id
        ? `company:${assignee.company_id}`
        : `email:${(assignee.email || assignee.name).toLowerCase()}`);

export const assigneesWithRole = (assignees: Assignee[] | undefined, role: AssigneeRole) =>
    (assignees ?? []).filter((a) => a.role === role);

/** Replaces one role's people, leaving the other roles as they were. */
export const withRoleAssignees = (
    assignees: Assignee[],
    role: AssigneeRole,
    people: Assignee[],
): Assignee[] => [
    ...assignees.filter((a) => a.role !== role),
    ...people.map((p) => ({ ...p, role })),
];

/** ISO date `days` from today, for the due-date default. */
export function dateInDays(days: number, from = new Date()): string {
    const date = new Date(from);
    date.setDate(date.getDate() + days);
    // Local calendar date: `toISOString` would slip a day west of UTC after dinner.
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Whole days from today to an ISO date; null when unset or unreadable. */
export function daysUntil(isoDate: string | null, from = new Date()): number | null {
    if (!isoDate) return null;
    const due = new Date(`${isoDate}T00:00:00`);
    if (Number.isNaN(due.getTime())) return null;
    const start = new Date(from);
    start.setHours(0, 0, 0, 0);
    return Math.round((due.getTime() - start.getTime()) / 86_400_000);
}
